import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useStore } from "../context/StoreContext";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const [state, setState] = useState("checking");
  const { clearCart } = useStore();
  const attempts = useRef(0);
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let stop = false;
    const poll = async () => {
      if (stop || attempts.current >= 12) { if (!stop) setState("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { clearCart(); setState("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("failed"); return; }
      } catch (e) { console.warn("Payment status poll failed:", e?.message); }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId, clearCart]);

  return (
    <div className="wrap cart-page" style={{ textAlign: "center" }}>
      {state === "checking" && (
        <div data-testid="payment-checking">
          <h1>CONFIRMING PAYMENT…</h1>
          <p className="sub">Hold on while we confirm your payment with Stripe.</p>
        </div>
      )}
      {state === "paid" && (
        <div data-testid="payment-success">
          <div style={{ fontSize: 54 }}>✓</div>
          <h1 style={{ color: "var(--land)" }}>PAYMENT SUCCESSFUL</h1>
          <p className="sub">You're enrolled — your courses are ready in My Learning.</p>
          <Link to="/my-learning" className="btn-primary btn-big" data-testid="go-my-learning-btn">Start Learning →</Link>
        </div>
      )}
      {(state === "failed" || state === "error") && (
        <div data-testid="payment-failed">
          <h1 style={{ color: "var(--ember)" }}>PAYMENT NOT COMPLETED</h1>
          <p className="sub">Your payment didn't go through. No charge was made.</p>
          <Link to="/cart" className="btn-primary">Back to Cart</Link>
        </div>
      )}
      {state === "timeout" && (
        <div data-testid="payment-timeout">
          <h1>STILL PROCESSING</h1>
          <p className="sub">Your payment is taking longer than usual (common with SEPA). Check My Learning in a few minutes — enrollment is automatic once it clears.</p>
          <Link to="/my-learning" className="btn-primary">Go to My Learning</Link>
        </div>
      )}
    </div>
  );
}

export function PaypalReturn() {
  const [params] = useSearchParams();
  const [state, setState] = useState("checking");
  const { clearCart } = useStore();
  const orderId = params.get("token");

  useEffect(() => {
    if (!orderId) { setState("failed"); return; }
    api.post(`/payments/paypal/capture/${orderId}`)
      .then((r) => { if (r.data.paid) { clearCart(); setState("paid"); } else setState("failed"); })
      .catch(() => setState("failed"));
  }, [orderId, clearCart]);

  return (
    <div className="wrap cart-page" style={{ textAlign: "center" }}>
      {state === "checking" && <div data-testid="paypal-checking"><h1>CONFIRMING PAYPAL PAYMENT…</h1><p className="sub">Capturing your payment.</p></div>}
      {state === "paid" && (
        <div data-testid="paypal-success">
          <div style={{ fontSize: 54 }}>✓</div>
          <h1 style={{ color: "var(--land)" }}>PAYMENT SUCCESSFUL</h1>
          <p className="sub">You're enrolled — your courses are ready in My Learning.</p>
          <Link to="/my-learning" className="btn-primary btn-big">Start Learning →</Link>
        </div>
      )}
      {state === "failed" && (
        <div data-testid="paypal-failed">
          <h1 style={{ color: "var(--ember)" }}>PAYMENT NOT COMPLETED</h1>
          <p className="sub">The PayPal payment couldn't be confirmed. No enrollment was made.</p>
          <Link to="/cart" className="btn-primary">Back to Cart</Link>
        </div>
      )}
    </div>
  );
}

export function PaymentCancel() {
  const nav = useNavigate();
  return (
    <div className="wrap cart-page" style={{ textAlign: "center" }} data-testid="payment-cancel">
      <h1>CHECKOUT CANCELLED</h1>
      <p className="sub">No worries — your cart is untouched.</p>
      <button className="btn-primary" onClick={() => nav("/cart")}>Back to Cart</button>
    </div>
  );
}
